function Section({ title, children }) {
  return (
    <section className="tc-section">
      <h2 className="tc-section__title">{title}</h2>
      {children}
    </section>
  );
}

function Sub({ title }) {
  return <h3 className="tc-sub">{title}</h3>;
}

function Body({ children }) {
  return <p className="tc-body">{children}</p>;
}

function Bullet({ children }) {
  return (
    <div className="tc-bullet">
      <span className="tc-bullet__dot">•</span>
      <span className="tc-bullet__text">{children}</span>
    </div>
  );
}

function ContactCard({ name, details }) {
  return (
    <div className="tc-contact">
      <p className="tc-contact__name">{name}</p>
      {details.map((d, i) => (
        <p key={i} className="tc-contact__detail">{d}</p>
      ))}
    </div>
  );
}

export default function TermsConditions() {
  return (
    <div id="terms-conditions" className="tc-container">
      <div className="tc-content">
        <p className="tc-last-updated">Last updated: May 5, 2026</p>

        <Section title="1. Agreement to Terms">
          <Body>
            By downloading, installing, or using the Dash Dynamic mobile application ("App"), you
            agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to these
            Terms, do not use the App. These Terms constitute a legally binding agreement between
            you ("User", "you") and Dash Dynamic Pvt. Ltd. ("Company", "we", "us", "our"), a
            company incorporated under the laws of India.
          </Body>
        </Section>

        <Section title="2. Eligibility">
          <Body>
            You must be at least 18 years of age to use this App. By using the App, you represent
            and warrant that you are 18 years of age or older and have the legal capacity to enter
            into a binding agreement. If you are using the App on behalf of an organisation, you
            represent that you are authorised to bind that organisation to these Terms.
          </Body>
        </Section>

        <Section title="3. Description of Service">
          <Body>
            Dash Dynamic provides a wireless electric vehicle (EV) charging platform that enables
            users to:
          </Body>
          <Bullet>Discover, connect to, and control Dash Dynamic wireless charging stations</Bullet>
          <Bullet>
            Monitor real-time charging telemetry (voltage, current, battery state of charge, energy
            delivered)
          </Bullet>
          <Bullet>Manage a digital wallet for charging fee payments</Bullet>
          <Bullet>View charging session history and usage records</Bullet>
          <Bullet>Communicate with our customer support team</Bullet>
          <Body>
            The Service is available in India and is designed primarily for two-wheeler electric
            vehicles.
          </Body>
        </Section>

        <Section title="4. Account Registration and Authentication">
          <Sub title="4.1 Registration" />
          <Body>
            You register using your mobile phone number (OTP-based), email address and password. You
            must provide accurate, current, and complete information and keep it updated.
          </Body>

          <Sub title="4.2 Account Security" />
          <Body>
            You are responsible for maintaining the confidentiality of your login credentials and
            for all activities that occur under your account. Notify us immediately at
            support@dashdynamic.in if you suspect any unauthorised access to your account.
          </Body>

          <Sub title="4.3 One Account Per User" />
          <Body>
            Each user may maintain only one account. Creating duplicate accounts is not permitted
            and may result in termination of all associated accounts.
          </Body>

          <Sub title="4.4 Account Verification" />
          <Body>
            We reserve the right to verify your identity and vehicle ownership at any time. Failure
            to complete verification may result in suspension of your account or access to certain
            features.
          </Body>
        </Section>

        <Section title="5. Vehicle Compatibility and User Responsibilities">
          <Sub title="5.1 Compatible Vehicles" />
          <Body>
            The Service is designed for electric two-wheelers compatible with Dash Dynamic wireless
            charging hardware. You are responsible for verifying that your vehicle is compatible
            before use. We are not liable for any damage resulting from use with an incompatible
            vehicle.
          </Body>

          <Sub title="5.2 Safe Use" />
          <Body>You agree to:</Body>
          <Bullet>
            Use charging stations only in accordance with provided instructions and safety guidelines
          </Bullet>
          <Bullet>Not tamper with, modify, or interfere with charging station hardware</Bullet>
          <Bullet>
            Ensure your vehicle is properly positioned on the charging pad as instructed
          </Bullet>
          <Bullet>
            Not use the Service in a manner that may endanger yourself, others, or property
          </Bullet>
          <Bullet>
            Comply with all applicable traffic, parking, and safety laws when using charging
            locations
          </Bullet>

          <Sub title="5.3 Bluetooth and Connectivity" />
          <Body>
            Some features require Bluetooth Low Energy (BLE) and an active internet connection. You
            are responsible for ensuring your device has the required permissions enabled. We are not
            liable for service interruptions caused by connectivity issues.
          </Body>
        </Section>

        <Section title="6. Wallet, Payments, and Billing">
          <Sub title="6.1 Wallet" />
          <Body>
            The App includes a prepaid digital wallet ("Wallet"). You may load funds using supported
            payment methods including UPI apps (PhonePe, Google Pay, Paytm) and other methods we
            may offer from time to time.
          </Body>

          <Sub title="6.2 Charging Fees" />
          <Body>
            Charging fees are deducted from your Wallet based on the energy consumed during each
            session (currently ₹10 per kWh). Rates are displayed in the App and may be updated with
            prior notice.
          </Body>

          <Sub title="6.3 No Refund Policy" />
          <Body>
            Wallet top-ups are non-refundable except where required by applicable law or in cases of
            technical error attributable to us. For disputed transactions, contact
            support@dashdynamic.in within 7 days of the transaction.
          </Body>

          <Sub title="6.4 Insufficient Balance" />
          <Body>
            If your Wallet balance is insufficient to complete a charging session, the session may
            be terminated automatically. We are not liable for any inconvenience caused by an
            insufficient balance.
          </Body>

          <Sub title="6.5 Payment Processing" />
          <Body>
            Payments are processed through third-party payment providers. By adding funds to your
            Wallet, you agree to the terms of the respective payment provider. We do not store your
            full payment instrument details on our servers.
          </Body>

          <Sub title="6.6 Taxes" />
          <Body>
            You are responsible for any applicable taxes on transactions made through the App,
            except for taxes we are required to collect from you by law.
          </Body>
        </Section>

        <Section title="7. Acceptable Use">
          <Body>You agree not to:</Body>
          <Bullet>Use the App for any unlawful purpose or in violation of any applicable laws</Bullet>
          <Bullet>
            Attempt to gain unauthorised access to any charging station, server, or network
          </Bullet>
          <Bullet>Reverse engineer, decompile, or disassemble any part of the App</Bullet>
          <Bullet>Use automated scripts, bots, or other tools to interact with the App</Bullet>
          <Bullet>Interfere with the proper operation of the App or the Service</Bullet>
          <Bullet>Use the App to transmit malware, viruses, or other harmful code</Bullet>
          <Bullet>Sublicense, sell, or transfer your account or access to the Service</Bullet>
          <Bullet>Provide false information during registration or verification</Bullet>
          <Bullet>
            Engage in any conduct that restricts or inhibits any other user's use of the Service
          </Bullet>
        </Section>

        <Section title="8. Intellectual Property">
          <Body>
            All content, features, and functionality of the App, including but not limited to
            software, text, graphics, logos, and trademarks, are the exclusive property of Dash
            Dynamic Pvt. Ltd. or its licensors and are protected under applicable intellectual
            property laws.
          </Body>
          <Body>
            You are granted a limited, non-exclusive, non-transferable, revocable licence to use the
            App solely for personal, non-commercial use in accordance with these Terms. No other
            rights are granted.
          </Body>
        </Section>

        <Section title="9. Third-Party Services and Links">
          <Body>
            The App may integrate with or link to third-party services (e.g., payment providers,
            WhatsApp, social media). We are not responsible for the content, privacy practices, or
            terms of any third-party service. Your use of third-party services is governed by their
            respective terms and privacy policies.
          </Body>
        </Section>

        <Section title="10. Disclaimers">
          <Sub title="10.1 Service Availability" />
          <Body>
            The Service is provided "as is" and "as available" without warranties of any kind,
            express or implied. We do not warrant that the Service will be uninterrupted,
            error-free, or free from viruses or other harmful components.
          </Body>

          <Sub title="10.2 Hardware Availability" />
          <Body>
            Charging station availability is not guaranteed. Stations may be offline, under
            maintenance, or unavailable due to factors beyond our control.
          </Body>

          <Sub title="10.3 Telemetry Data" />
          <Body>
            Real-time charging data (voltage, current, state of charge) is provided for
            informational purposes only and should not be solely relied upon for vehicle battery
            management.
          </Body>
        </Section>

        <Section title="11. Limitation of Liability">
          <Body>
            To the fullest extent permitted by applicable law, Dash Dynamic Pvt. Ltd., its
            directors, employees, and agents shall not be liable for any:
          </Body>
          <Bullet>Indirect, incidental, special, or consequential damages</Bullet>
          <Bullet>Loss of data, profits, revenue, or business</Bullet>
          <Bullet>
            Damage to your vehicle, charging equipment, or property arising from use of the
            wireless charging service — including damage arising from BLE connectivity loss, delayed
            or failed start/stop commands, or interrupted telemetry — except where such damage is
            caused by our proven negligence or wilful default
          </Bullet>
          <Bullet>
            Losses arising from your failure to comply with safety guidelines, vehicle positioning
            instructions, or vehicle compatibility requirements
          </Bullet>
          <Body>
            Our total aggregate liability for any claim shall not exceed the amount paid by you to
            us in the three (3) months preceding the event giving rise to the claim.
          </Body>
        </Section>

        <Section title="12. Indemnification">
          <Body>
            You agree to indemnify and hold harmless Dash Dynamic Pvt. Ltd. and its officers,
            directors, employees, and agents from any claims, damages, losses, liabilities, costs,
            and expenses (including legal fees) arising from:
          </Body>
          <Bullet>Your use of the App or Service</Bullet>
          <Bullet>Your violation of these Terms</Bullet>
          <Bullet>Your violation of any rights of a third party</Bullet>
          <Bullet>Any false or inaccurate information you provide</Bullet>
        </Section>

        <Section title="13. Suspension and Termination">
          <Sub title="13.1 Termination by You" />
          <Body>
            You may delete your account at any time by contacting us at support@dashdynamic.in. Any
            unused Wallet balance at the time of account deletion will be handled in accordance with
            our refund policy and applicable law.
          </Body>

          <Sub title="13.2 Termination by Us" />
          <Body>
            We may suspend or terminate your account immediately, without prior notice or liability,
            if you breach these Terms, engage in fraudulent activity, or for any other reason at our
            sole discretion.
          </Body>

          <Sub title="13.3 Effect of Termination" />
          <Body>
            Upon termination, your right to use the App and Service ceases immediately. Provisions
            of these Terms that by their nature should survive termination shall survive.
          </Body>
        </Section>

        <Section title="14. Modifications to the Service and Terms">
          <Body>
            We reserve the right to modify or discontinue the Service at any time with or without
            notice. Material changes to these Terms will be communicated via the App or registered
            email address. Continued use of the App after changes take effect constitutes acceptance
            of the revised Terms.
          </Body>
        </Section>

        <Section title="15. Force Majeure">
          <Body>
            We shall not be liable for any failure or delay in performance under these Terms
            resulting from causes beyond our reasonable control, including but not limited to acts
            of God, natural disasters, power outages, telecommunications or internet failures,
            failures of third-party payment providers, government action, labour disputes, or
            pandemics. Our obligations under these Terms will be suspended for the duration of such
            an event.
          </Body>
        </Section>

        <Section title="16. Governing Law and Dispute Resolution">
          <Sub title="16.1 Governing Law" />
          <Body>
            These Terms are governed by and construed in accordance with the laws of India,
            including the Information Technology Act, 2000, the Consumer Protection Act, 2019, and
            the Digital Personal Data Protection Act, 2023.
          </Body>

          <Sub title="16.2 Good-Faith Negotiation" />
          <Body>
            In the event of any dispute, the parties shall first attempt to resolve the matter
            through good-faith negotiation for a period of 30 days.
          </Body>

          <Sub title="16.3 Arbitration" />
          <Body>
            If a dispute is not resolved through negotiation within 30 days, it shall be referred to
            and finally resolved by arbitration under the Arbitration and Conciliation Act, 1996,
            conducted by a sole arbitrator appointed by mutual agreement of the parties. The seat and
            venue of arbitration shall be New Delhi, India, and the language of arbitration shall be
            English. The arbitrator's award shall be final and binding on both parties, subject to
            the exclusive jurisdiction of the competent courts in New Delhi, India for any matter not
            subject to arbitration or for enforcement of the award.
          </Body>
        </Section>

        <Section title="17. Severability">
          <Body>
            If any provision of these Terms is held to be invalid, illegal, or unenforceable by a
            court or arbitrator of competent jurisdiction, that provision shall be modified to the
            minimum extent necessary to make it enforceable, or severed if it cannot be so modified,
            and the remaining provisions of these Terms shall continue in full force and effect.
          </Body>
        </Section>

        <Section title="18. Entire Agreement and Waiver">
          <Body>
            These Terms, together with our Privacy Policy, constitute the entire agreement between
            you and Dash Dynamic Pvt. Ltd. regarding your use of the App and Service, and supersede
            any prior agreements or understandings. Our failure to enforce any right or provision of
            these Terms shall not be considered a waiver of that right or provision, and any waiver
            must be in writing and signed by an authorised representative of Dash Dynamic Pvt. Ltd.
            to be effective.
          </Body>
        </Section>

        <Section title="19. Grievance Officer">
          <Body>
            In accordance with the Information Technology Act, 2000, the following Grievance Officer
            is appointed:
          </Body>
          <ContactCard
            name="Robin Singh"
            details={[
              "robin.singh@dashdynamic.in",
              "2-B-5C Research and Innovation Park, IIT Flyover, Hauzkhas, New Delhi, India",
            ]}
          />
          <Body>We will endeavour to redress complaints within 30 days of receipt.</Body>
        </Section>

        <Section title="20. Contact Us">
          <ContactCard
            name="Dash Dynamic Pvt. Ltd."
            details={["support@dashdynamic.in", "www.dashdynamic.in"]}
          />
        </Section>
      </div>
    </div>
  );
}